"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  createPreAppointmentForm_apiCalls,
  getPreAppointmentFormByAppointmentId_apiCalls,
  updatePreAppointmentForm_apiCalls,
} from "../../../api/api_calls/appointment_apiCalls";
import CheckCircle from "../../../assets/images/Check.png";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Form } from "../../../components/ui/form";
import { useToast } from "../../../hooks/use-toast";
import Step1Form from "./components/Step1TodaysVisit";
import Step2Form from "./components/Step2BetweenVisits";
import Step3Form from "./components/Step3Lifestyle";
import Step4Form from "./components/Step4Update";

const PatientQuestionnaireSchema = z.object({
  // Required appointmentId
  appointmentId: z.string().min(1, "Appointment ID is required"),

  // Step 1 - Today's Visit
  goalForHealth: z.string().optional(),
  agendaForAppointment: z.string().optional(),
  isMedCertificateRequested: z.boolean().optional().default(false),

  // Diabetes
  diabetes_isProblemWithMedication: z.boolean().nullable().optional(),
  diabetes_homeGlucoseReadings: z.string().optional().default(""),

  // High BP
  highBloodPressure_isProblemWithMedication: z.boolean().nullable().optional(),
  highBloodPressure_homeBpReadings: z.string().optional().default(""),

  // Depression
  depression_isProblemWithMedication: z.boolean().nullable().optional(),
  depression_isSuicidalThoughts: z.boolean().nullable().optional(),

  // High Cholesterol
  highCholesterol_isProblemWithMedication: z.boolean().nullable().optional(),

  // Step 2 - Between Visits
  otherMedVisit: z.boolean().nullable().optional(),
  otherMedVisit_details: z.string().optional().default(""),

  // Step 3 - Lifestyle
  exercice: z.string().optional().default(""),
  exercice_howLong: z.string().optional().default(""),
  exercice_howOften: z.string().optional().default(""),
  isShortnessOfBreath: z.boolean().nullable().optional(),

  // Smoking
  smoking_isInterestedInQuitting: z.boolean().nullable().optional(),
  smoking_howMuch: z.string().optional().default(""),

  // Alcohol
  alcohol_numberOfDrinks_week: z.string().optional().default(""),
  alcohol_numberOfDrinks_day: z.string().optional().default(""),
  alcohol_moreThanFourDrinks: z.boolean().nullable().optional(),
  alcohol_othersConcerned: z.boolean().nullable().optional(),

  // Falls
  fallenInPastyear: z.boolean().nullable().optional(),
  problemWithWalkingBalance: z.boolean().nullable().optional(),

  // Caffeine
  caffeinePerDay: z.string().optional().default(""),

  // Birth Control
  birthControlMethod: z.string().optional().default(""),

  // Safety
  isUnsafeRelationship: z.boolean().nullable().optional(),
  isWearSeatbelt: z.boolean().nullable().optional(),

  // HIV
  isHivTestRequested: z.boolean().nullable().optional(),

  // Sleep
  isSleepApnea: z.boolean().nullable().optional(),

  // Depression Screen
  isFeelingDown: z.boolean().nullable().optional(),

  // Medication
  isProblemWithMedication: z.boolean().nullable().optional(),
  problemWithMedicationDetails: z.string().optional().default(""),

  // Bladder Control
  isProblemBladderControl: z.boolean().nullable().optional(),

  // End of Life Care
  discussEndOfLifeCare: z.boolean().nullable().optional(),

  // Step 4 - Update
  newIllnessInFamily: z.string().optional().default(""),
  newDrugAllergies: z.string().optional().default(""),
  specificIssues: z.string().optional().default(""),

  // Symptoms (arrays matching backend enums)
  constitutionalSymptoms: z.array(z.string()).optional().default([]),
  eyeProblems: z.array(z.string()).optional().default([]),
  enmtProblems: z.array(z.string()).optional().default([]),
  cardiovascularProblems: z.array(z.string()).optional().default([]),
  respiratoryProblems: z.array(z.string()).optional().default([]),
  gastrointestinalProblems: z.array(z.string()).optional().default([]),
  genitourinaryProblems: z.array(z.string()).optional().default([]),
  skinProblems: z.array(z.string()).optional().default([]),
  sleepProblems: z.array(z.string()).optional().default([]),
  neurologicalProblems: z.array(z.string()).optional().default([]),
  musculoskeletalProblems: z.array(z.string()).optional().default([]),
  psychiatricProblems: z.array(z.string()).optional().default([]),
  endocrineProblems: z.array(z.string()).optional().default([]),
  hematologicProblems: z.array(z.string()).optional().default([]),
  allergicProblems: z.array(z.string()).optional().default([]),
});

const PatientQuestionnaire = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [existingFormData, setExistingFormData] = useState(null);
  const { toast } = useToast();
  const firstInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Get appointmentId from navigation state
  const appointmentId = location.state?.appointmentId;
  console.log("appointmentId :", appointmentId);

  const form = useForm({
    resolver: zodResolver(PatientQuestionnaireSchema),
    defaultValues: {
      // Required appointmentId
      appointmentId: appointmentId || "",

      // Step 1 - Today's Visit
      goalForHealth: "",
      agendaForAppointment: "",
      isMedCertificateRequested: false,
      diabetes_isProblemWithMedication: null,
      diabetes_homeGlucoseReadings: "",
      highBloodPressure_isProblemWithMedication: null,
      highBloodPressure_homeBpReadings: "",
      depression_isProblemWithMedication: null,
      depression_isSuicidalThoughts: null,
      highCholesterol_isProblemWithMedication: null,

      // Step 2 - Between Visits
      otherMedVisit: null,
      otherMedVisit_details: "",

      // Step 3 - Lifestyle
      exercice: "",
      exercice_howLong: "",
      exercice_howOften: "",
      isShortnessOfBreath: null,
      smoking_isInterestedInQuitting: null,
      smoking_howMuch: "",
      alcohol_numberOfDrinks_week: "",
      alcohol_numberOfDrinks_day: "",
      alcohol_moreThanFourDrinks: null,
      alcohol_othersConcerned: null,
      fallenInPastyear: null,
      problemWithWalkingBalance: null,
      caffeinePerDay: "",
      birthControlMethod: "",
      isUnsafeRelationship: null,
      isWearSeatbelt: null,
      isHivTestRequested: null,
      isSleepApnea: null,
      isFeelingDown: null,
      isProblemWithMedication: null,
      problemWithMedicationDetails: "",
      isProblemBladderControl: null,
      discussEndOfLifeCare: null,

      // Step 4 - Update
      newIllnessInFamily: "",
      newDrugAllergies: "",
      specificIssues: "",
      constitutionalSymptoms: [],
      eyeProblems: [],
      enmtProblems: [],
      cardiovascularProblems: [],
      respiratoryProblems: [],
      gastrointestinalProblems: [],
      genitourinaryProblems: [],
      skinProblems: [],
      sleepProblems: [],
      neurologicalProblems: [],
      musculoskeletalProblems: [],
      psychiatricProblems: [],
      endocrineProblems: [],
      hematologicProblems: [],
      allergicProblems: [],
    },
    mode: "onChange",
  });

  // Function to fetch existing pre-appointment form data
  const fetchExistingFormData = useCallback(async () => {
    if (!appointmentId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response = await getPreAppointmentFormByAppointmentId_apiCalls({
        appointmentId,
      });
      if (response?.data) {
        setExistingFormData(response.data);
        // Clean up the data for the form
        const formData = Object.keys(response.data).reduce((acc, key) => {
          if (!["id", "createdAt", "updatedAt"].includes(key)) {
            acc[key] = response.data[key];
          }
          return acc;
        }, {});
        formData.appointmentId = appointmentId;
        form.reset(formData);
      } else {
        setExistingFormData(null);
      }
    } catch (error) {
      console.error("Failed to fetch pre-appointment form:", error);
      setExistingFormData(null);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, form]);

  // Auto-focus first input on step change
  useEffect(() => {
    if (firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current.focus();
      }, 100);
    }
  }, [activeStep]);

  // Fetch existing form data on component mount
  useEffect(() => {
    fetchExistingFormData();
  }, [fetchExistingFormData]);

  // Check if appointmentId is available
  useEffect(() => {
    if (!appointmentId) {
      toast({
        title: "Missing Appointment Information",
        description:
          "Please book an appointment first before filling the questionnaire.",
        variant: "destructive",
      });
      // Redirect to find doctor page after 3 seconds
      setTimeout(() => {
        navigate("/patient/find-doctor", { replace: true });
      }, 3000);
    }
  }, [appointmentId, toast, navigate]);

  // Get field validation schema for the current step

  const handleNext = async () => {
    try {
      if (activeStep < 4) {
        setActiveStep(activeStep + 1);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  const handlePrevious = useCallback(() => {
    setActiveStep(activeStep - 1);
    window.scrollTo(0, 0);
  }, [activeStep]);

  const uploadForm = async (values) => {
    console.log("activeStep", activeStep);

    setIsSubmitting(true);
    try {
      if (!values.appointmentId) throw new Error("Appointment ID is required");
      let response;
      if (existingFormData?.id) {
        response = await updatePreAppointmentForm_apiCalls({
          id: existingFormData.id,
          ...values,
        });
        if (response?.data) setExistingFormData(response.data);
      } else {
        response = await createPreAppointmentForm_apiCalls(values);
        if (response?.data) setExistingFormData(response.data);
      }

      toast({
        title: "Success",
        description: `Pre-appointment form ${existingFormData?.id ? "updated" : "submitted"} successfully!`,
        variant: "default",
      });

      setIsSuccessDialogOpen(true);
      setTimeout(
        () =>
          navigate("/patient/appointments-details", {
            state: { appointmentId },
            replace: true,
          }),
        2000,
      );

      // Only reset form after create, not after update
      if (!existingFormData?.id) {
        form.reset();
        setActiveStep(1);
      }
    } catch (error) {
      console.error("Full error:", {
        message: error.message,
        response: error.response?.data,
        stack: error.stack,
      });
      toast({
        title: "Error",
        description:
          error?.message || "Failed to submit form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the appropriate step component based on activeStep
  const renderStepComponent = () => {
    switch (activeStep) {
      case 1:
        return (
          <Step1Form
            form={form}
            firstInputRef={firstInputRef}
            isSubmitting={isSubmitting}
          />
        );
      case 2:
        return (
          <Step2Form
            form={form}
            firstInputRef={firstInputRef}
            isSubmitting={isSubmitting}
          />
        );
      case 3:
        return (
          <Step3Form
            form={form}
            firstInputRef={firstInputRef}
            isSubmitting={isSubmitting}
          />
        );
      case 4:
        return (
          <Step4Form
            form={form}
            firstInputRef={firstInputRef}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // Show loading state while fetching existing data
  if (isLoading) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="container mx-auto px-4 py-6">
          <div className="bg-white rounded-lg shadow-sm border p-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mr-3" />
              <span className="text-lg">Loading form data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-4">
          {activeStep > 1 && (
            <button
              className="p-2 rounded-full border hover:bg-gray-100 transition-colors"
              onClick={handlePrevious}
              aria-label="Go back"
              disabled={isSubmitting}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <Button
            variant="outline"
            type="button"
            className="ml-auto bg-gray-100 text-gray-700 hover:bg-gray-200"
            disabled={isSubmitting}
            onClick={() =>
              navigate("/patient/appointments-details", {
                state: { appointmentId },
                replace: true,
              })
            }
          >
            Skip
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 max-w-4xl mx-auto">
          <h1 className="text-center text-lg font-medium mb-8">
            Please Provide following detail for better understanding
          </h1>

          {/* Progress Steps - Improved for mobile */}
          <div className="flex flex-wrap justify-center items-center mb-8 gap-2">
            {[
              { label: "Today's Visit", step: 1 },
              { label: "Between Visits", step: 2 },
              { label: "Lifestyle", step: 3 },
              { label: "Update", step: 4 },
            ].map(({ label, step }, idx) => (
              <div className="flex items-center" key={label}>
                <button
                  type="button"
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold transition-colors border
                    ${activeStep === step ? "bg-primary text-white" : "bg-white text-primary border-primary"}
                    ${activeStep > step ? "cursor-pointer" : ""}
                  `}
                  onClick={() => {
                    setActiveStep(step);
                  }}
                  aria-current={activeStep === step ? "step" : undefined}
                >
                  {step}
                </button>
                <span className="ml-2 mr-4 font-medium text-sm sm:text-base">
                  {label}
                </span>
                {idx < 3 && (
                  <div className="w-8 sm:w-16 border-t border-dashed border-gray-300 hidden sm:block"></div>
                )}
              </div>
            ))}
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(uploadForm)}
              className="space-y-6"
            >
              <div className="border-solid border-[1px] border-[#E7E8E9] p-6 rounded-lg">
                {renderStepComponent()}
              </div>

              <div className="flex justify-end mt-6">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setIsCancelModalOpen(true)}
                    disabled={isSubmitting}
                    className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </Button>

                  {activeStep === 4 ? (
                    <Button
                      variant="default"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      type="submit"
                      onClick={() =>
                        console.log("Save button clicked", {
                          isSubmitting,
                          formState: form.formState,
                          existingFormData,
                        })
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                  ) : (
                    <button
                      type="button" // Explicitly set type to button
                      onClick={handleNext}
                      className="bg-[#0052FD] rounded-lg text-primary-text shadow hover:bg-primary-hover/80 h-9 px-4 py-2"
                      disabled={isSubmitting}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>

      {/* Success Dialog */}
      <SuccessDialog
        isOpen={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
        isUpdate={!!existingFormData?.id}
      />

      <ConfirmationModal
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onConfirm={() => {
          setIsCancelModalOpen(false);
          navigate("/patient/appointments-details", {
            state: { appointmentId },
            replace: true,
          });
        }}
        title="Cancel Questionnaire?"
        description="Are you sure you want to cancel? All unsaved changes will be lost."
        confirmText="Yes, Cancel"
        cancelText="No, Go Back"
      />
    </div>
  );
};

// Success Dialog Component
const SuccessDialog = ({ isOpen, onClose, isUpdate = false }) => {
  const message = isUpdate
    ? "Your pre-appointment form has been updated successfully"
    : "Your pre-appointment form has been submitted successfully";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[400px] w-[400px] overflow-auto p-0 flex items-center justify-center flex-col px-6">
        <DialogTitle className="sr-only">Patient Information</DialogTitle>
        <DialogDescription className="sr-only">
          Enter or update your personal and medical information
        </DialogDescription>
        <img
          alt="Success"
          src={CheckCircle || "/placeholder.svg"}
          className="w-24 h-24"
        />
        <h2 className="font-bold text-[#16A34A] text-[20px]">Success</h2>
        <h2 className="font-[600] text-[#1E1E1E] text-[16px] text-center">
          {message}
        </h2>
      </DialogContent>
    </Dialog>
  );
};

export default PatientQuestionnaire;
